import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, ChevronRight, FileText } from 'lucide-react';
import { assessmentApi, queryKeys } from '../services/api';
import dayjs from 'dayjs';

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  DRAFT:     { label: 'Nháp',          color: 'bg-slate-100 dark:bg-slate-500/10 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-500/20' },
  SUBMITTED: { label: 'Đã nộp',        color: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/20' },
  AUDITING:  { label: 'Đang kiểm tra', color: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/20' },
  CONCLUDED: { label: 'Đã kết luận',   color: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20' },
  ARCHIVED:  { label: 'Lưu trữ',       color: 'bg-gray-100 dark:bg-gray-500/10 text-gray-500 dark:text-gray-500 border-gray-200 dark:border-gray-500/20' },
};

export default function AssessmentListPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus]   = useState('');
  const [ranking, setRanking] = useState('');
  const [page, setPage]       = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.sessions.list({ search, status, ranking, page }),
    queryFn:  () => assessmentApi.list({ status: status || undefined, ranking: ranking || undefined, page, limit: 20 }),
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl text-slate-900 dark:text-white tracking-tight leading-tight font-heading">Hồ sơ kiểm toán</h2>
           <p className="text-slate-500 dark:text-slate-400 mt-1 font-light">Quản lý và theo dõi hồ sơ kiểm toán tuân thủ pháp luật dữ liệu</p>
        </div>
        <button className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-sm font-medium shadow-[0_10px_30px_rgba(99,102,241,0.3)] hover:shadow-[0_15px_40px_rgba(99,102,241,0.5)] transition-all transform hover:-translate-y-0.5 tracking-tight">
          <Plus size={16} /> Tạo hồ sơ mới
        </button>
      </div>

      <div className="glass-panel p-6 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap mb-6">
          <div className="flex items-center gap-2 bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2.5 flex-1 min-w-[250px] focus-within:border-indigo-500/50 transition-all shadow-inner">
            <Search size={16} className="text-slate-400" />
            <input 
              value={search} 
              onChange={e => setSearch(e.target.value)}
              placeholder="Tìm theo đơn vị, mã hồ sơ..."
              className="text-sm outline-none flex-1 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 font-light"
            />
          </div>
          
          <div className="relative">
            <select 
              value={status} 
              onChange={e => setStatus(e.target.value)}
              className="appearance-none bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm rounded-xl px-4 py-2.5 pr-10 outline-none focus:border-indigo-500/50 transition-all w-48 shadow-inner font-light"
            >
              <option value="" className="bg-white dark:bg-[#10101a]">Tất cả trạng thái</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k} className="bg-white dark:bg-[#10101a]">{v.label}</option>
              ))}
            </select>
            <Filter size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>

          <div className="relative">
            <select 
              value={ranking} 
              onChange={e => setRanking(e.target.value)}
              className="appearance-none bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm rounded-xl px-4 py-2.5 pr-10 outline-none focus:border-indigo-500/50 transition-all w-40 shadow-inner font-light"
            >
              <option value="" className="bg-white dark:bg-[#10101a]">Tất cả xếp hạng</option>
              <option value="A" className="bg-white dark:bg-[#10101a]">Loại A</option>
              <option value="B" className="bg-white dark:bg-[#10101a]">Loại B</option>
              <option value="C" className="bg-white dark:bg-[#10101a]">Loại C</option>
            </select>
            <Filter size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-white/10">
                {['Mã hồ sơ', 'Đơn vị', 'Kỳ', 'Trạng thái', 'Điểm', 'Xếp hạng', 'Ngày tạo', ''].map(h => (
                  <th key={h} className="text-left text-[10px] font-black text-slate-400 dark:text-slate-500 px-4 py-4 tracking-tight whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {isLoading && (
                <tr>
                  <td colSpan={8} className="text-center py-20 text-slate-400 font-bold">
                    <div className="flex flex-col items-center gap-3">
                       <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                       <span className="text-xs font-black tracking-tight leading-loose">Đang kết nối dữ liệu máy chủ...</span>
                    </div>
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={8} className="text-center py-20 text-rose-500 text-xs font-black tracking-tight leading-loose">
                    Đã xảy ra lỗi khi tải dữ liệu. Vui lòng thử lại sau.
                  </td>
                </tr>
              )}
              {data?.data?.map(s => (
                <tr key={s.id} className="group hover:bg-slate-50 dark:hover:bg-white/5 transition-all">
                  <td className="px-4 py-5 font-mono text-xs font-black text-indigo-600 dark:text-indigo-400 tracking-tighter">{s.sessionCode}</td>
                  <td className="px-4 py-5">
                    <p className="font-black text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition-colors tracking-tight text-[13px]">{s.organization?.name ?? '—'}</p>
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 mt-0.5 tracking-tight">{s.organization?.code}</p>
                  </td>
                  <td className="px-4 py-5 font-bold text-slate-600 dark:text-slate-400 whitespace-nowrap text-xs tracking-tight">{s.period?.name ?? '—'}</td>
                  <td className="px-4 py-5">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black border tracking-tighter shadow-sm ${STATUS_LABEL[s.status]?.color}`}>
                      {STATUS_LABEL[s.status]?.label ?? s.status}
                    </span>
                  </td>
                  <td className="px-4 py-5">
                    <span className="font-black text-slate-900 dark:text-white text-lg">{s.totalScore ?? '—'}</span>
                    <span className="text-slate-400 dark:text-slate-600 text-[10px] font-black ml-1">/100</span>
                  </td>
                  <td className="px-4 py-5">
                    {s.ranking && (
                      <span className={`px-2 py-0.5 rounded border text-[10px] font-black tracking-tighter shadow-sm bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20`}>
                        Loại {s.ranking}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-5 text-slate-500 dark:text-slate-500 whitespace-nowrap text-[10px] font-black tracking-tight">
                    {dayjs(s.createdAt).format('DD/MM/YYYY')}
                  </td>
                  <td className="px-4 py-5 text-right">
                    <Link 
                      to={`/assessments/${s.id}`} 
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 hover:bg-indigo-500 hover:text-white dark:hover:bg-indigo-500 text-slate-600 dark:text-slate-400 transition-all text-[10px] font-medium tracking-tight shadow-sm active:scale-95"
                    >
                      Chi tiết <ChevronRight size={14} />
                    </Link>
                  </td>
                </tr>
              ))}
              {data?.data?.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-24 text-slate-400">
                    <FileText size={48} className="mx-auto mb-4 opacity-10" />
                    <p className="text-xs font-black tracking-tight leading-loose">Hệ thống chưa ghi nhận hồ sơ kiểm toán nào</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Footer */}
        {data && data.meta.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 pt-6 mt-4 border-t border-slate-200 dark:border-white/5">
            <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-tight leading-loose">
              Tổng cộng <span className="text-slate-900 dark:text-white">{data.meta.total}</span> hồ sơ được ghi nhận
            </p>
            <div className="flex gap-2">
              {Array.from({ length: Math.min(data.meta.totalPages, 5) }, (_, i) => i + 1).map(p => (
                <button 
                  key={p} 
                  onClick={() => setPage(p)}
                  className={`w-9 h-9 rounded-xl text-[10px] font-black transition-all border ${
                    page === p 
                      ? 'bg-indigo-500 text-white border-indigo-400 shadow-[0_5px_15px_rgba(99,102,241,0.3)]' 
                      : 'bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-white/5 hover:bg-slate-100 dark:hover:bg-white/10'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
